import { lazy } from 'react';
import { Navigate, RouteObject, Outlet } from 'react-router-dom';
import LimitedByPlan from '../components/Middlewares/LimitedByPlan';
import ErrorBoundary from '../components/ErrorBoundary';

const Index = lazy(() => import('../pages/Index'));
const Accounts = lazy(() => import('../pages/Accounts'));
const Recurring = lazy(() => import('../pages/Transactions/Recurring'));
const Variables = lazy(() => import('../pages/Transactions/Variables'));
const Payments = lazy(() => import('../pages/Transactions/Payments'));
const AccountSetting = lazy(() => import('../pages/Users/AccountSetting'));
const HouseholdSettings = lazy(() => import('../pages/Settings/HouseholdSettings'));
const SubscriptionPlans = lazy(() => import('../pages/Users/SubscriptionPlans'));
const AnnualTotal = lazy(() => import('../pages/AnnualTotal'));
const Calendar = lazy(() => import('../pages/Calendar'));
const MonthlyStats = lazy(() => import('../pages/MonthlyStats'));
const ERROR404 = lazy(() => import('../pages/Error404'));
const Login = lazy(() => import('../pages/Authentication/Login'));
const Register = lazy(() => import('../pages/Authentication/Register'));
const ForgotPassword = lazy(() => import('../pages/Authentication/ForgotPassword'));
const YandexCallback = lazy(() => import('../pages/Authentication/YandexCallback'));

const BufferBudget = lazy(() => import('../pages/Tools/BufferBudget'));
const InformalDebt = lazy(() => import('../pages/Tools/InformalDebt'));
const GoalPrioritizationMatrix = lazy(() => import('../pages/Tools/GoalPrioritizationMatrix'));
const FinancialHabitTracker = lazy(() => import('../pages/Tools/FinancialHabitTracker'));
const EmotinalSpending = lazy(() => import('../pages/Tools/EmotionalSpending'));
const SubscriptionTracker = lazy(() => import('../pages/Tools/SubscriptionTracker'));
const AuthActionHandler = lazy(() => import('../pages/AuthActionHandler'));
import LegacyVault from '../pages/Tools/LegacyVault';
import DefaultLayout from '../components/Layouts/DefaultLayout';
import BlankLayout from '../components/Layouts/BlankLayout';
const TermsAndCondition = lazy(() => import('../pages/Legal/TermsAndCondition'));
const PaymentStatus = lazy(() => import('../pages/PaymentStatus'));
const EmailVerificationResult = lazy(() => import('../pages/EmailVerificationResult'));
const PendingVerification = lazy(() => import('../pages/PendingVerification'));
const ConsciousSpendingPlan = lazy(() => import('../pages/BuildYourWealth/ConsciousSpendingPlan'));
const PdfColumnMapping = lazy(() => import('../pages/PdfColumnMapping'));
const AppGuide = lazy(() => import('../pages/AppGuide'));
const LifestyleCreepReport = lazy(() => import('../pages/Reports/LifestyleCreepReport'));
const EmotionalSpendingAnalysis = lazy(() => import('../pages/Reports/EmotionalSpendingAnalysis'));
const CashFlowForecast = lazy(() => import('../pages/Reports/CashFlowForecast'));
const AffordabilityCalculator = lazy(() => import('../pages/BuildYourWealth/AffordabilityCalculator'));
const RecommendationsHub = lazy(() => import('../pages/BuildYourWealth/RecommendationsHub'));
const BudgetManager = lazy(() => import('../pages/Budget/BudgetManager'));
const NetWorthReport = lazy(() => import('../pages/Reports/NetWorthReport'));
const BudgetPerformance = lazy(() => import('../pages/Reports/BudgetPerformance'));
const SpendingComparison = lazy(() => import('../pages/Reports/SpendingComparison'));
const DebtPayoffCalculator = lazy(() => import('../pages/Tools/DebtPayoffCalculator'));
const DatabaseCleanup = lazy(() => import('../components/DatabaseCleanup'));
const BoadFinance = lazy(() => import('../pages/Integrations/BoadFinance'));
const SinkingFunds = lazy(() => import('../pages/BuildYourWealth/SinkingFunds'));
const InvestmentsDashboard = lazy(() => import('../pages/Investments/Dashboard'));
const AdminDashboard = lazy(() => import('../pages/Admin/Dashboard'));
const AdminReferrals = lazy(() => import('../pages/Admin/Referrals'));
const AdminUsers = lazy(() => import('../pages/Admin/Users'));
const AdminFinance = lazy(() => import('../pages/Admin/Finance'));
const AdminSettings = lazy(() => import('../pages/Admin/Settings'));
const AdminOffers = lazy(() => import('../pages/Admin/Offers'));
const MigrationTool = lazy(() => import('../components/migration/MigrationTool'));

const AuthRequired = lazy(() => import('../components/Middlewares/AuthRequired'));
const AdminRequired = lazy(() => import('../components/Middlewares/AdminRequired'));
const LogoutNeeded = lazy(() => import('../components/Middlewares/LogoutNeeded'));
const BasicModeRestricted = lazy(() => import('../components/Middlewares/BasicModeRestricted'));

type Route = RouteObject & {
    layout?: 'default' | 'blank';
};

const routes: Route[] = [
    {
        path: '/',
        element: <Navigate to="/login" replace />,
    },

    // dashboard
    {
        path: 'dashboard',
        element: (
            <AuthRequired>
                <ErrorBoundary>
                    <DefaultLayout />
                </ErrorBoundary>
            </AuthRequired>
        ),
        children: [
            // Dashboard features
            { index: true, element: <Navigate to="/dashboard/overview" replace /> },
            { path: 'overview', element: <Index /> },
            { path: 'index', element: <Navigate to="/dashboard/overview" replace /> }, // Redirect old route
            { path: 'annual-total', element: <BasicModeRestricted><AnnualTotal /></BasicModeRestricted> },
            { path: 'calendar', element: <BasicModeRestricted><Calendar /></BasicModeRestricted> },
            { path: 'accounts', element: <Accounts /> },
            { path: 'investments', element: <BasicModeRestricted><InvestmentsDashboard /></BasicModeRestricted> },
            { path: 'budget-manager', element: <BasicModeRestricted><BudgetManager /></BasicModeRestricted> },
            {
                path: 'transactions',
                children: [
                    { path: 'recurring', element: <BasicModeRestricted><Recurring /></BasicModeRestricted> },
                    { path: 'variables', element: <Variables /> },
                    { path: 'payments', element: <BasicModeRestricted><Payments /></BasicModeRestricted> },
                ],
            },
            { path: 'monthly-stats/:year/:month', element: <BasicModeRestricted><MonthlyStats /></BasicModeRestricted> },
            { path: 'pdf/map-columns', element: <PdfColumnMapping /> },

            {
                path: 'tools',
                element: (
                    <BasicModeRestricted>
                        <Outlet />
                    </BasicModeRestricted>
                ),
                children: [
                    { path: 'emotional-spending', element: <EmotinalSpending /> },
                    { path: 'buffer-budget', element: <BufferBudget /> },
                    { path: 'informal-debt', element: <InformalDebt /> },
                    { path: 'goal-prioritization-matrix', element: <GoalPrioritizationMatrix /> },
                    { path: 'habit-tracker', element: <FinancialHabitTracker /> },
                    { path: 'subscription-tracker', element: <SubscriptionTracker /> },
                    { path: 'legacy-vault', element: <LegacyVault /> },
                    { path: 'debt-payoff-calculator', element: <DebtPayoffCalculator /> },
                ],
            },
            {
                path: 'users',
                children: [
                    { path: 'account-settings', element: <AccountSetting /> },
                    { path: 'household-settings', element: <BasicModeRestricted><HouseholdSettings /></BasicModeRestricted> },
                    { path: 'subscription-plans', element: <SubscriptionPlans /> },
                ],
            },
            { path: 'app-guide', element: <BasicModeRestricted><AppGuide /></BasicModeRestricted> },
            {
                path: 'build-your-wealth',
                element: (
                    <BasicModeRestricted>
                        <Outlet />
                    </BasicModeRestricted>
                ),
                children: [
                    { path: 'conscious-spending-plan', element: <ConsciousSpendingPlan /> },
                    { path: 'sinking-funds', element: <SinkingFunds /> },
                    { path: 'affordability-calculator', element: <AffordabilityCalculator /> },
                    { path: 'recommendations', element: <RecommendationsHub /> },
                ]
            },
            { path: 'reports/lifestyle-creep', element: <BasicModeRestricted><LifestyleCreepReport /></BasicModeRestricted> },
            { path: 'reports/emotional-spending', element: <BasicModeRestricted><EmotionalSpendingAnalysis /></BasicModeRestricted> },
            { path: 'reports/cash-flow', element: <BasicModeRestricted><CashFlowForecast /></BasicModeRestricted> },
            { path: 'reports/net-worth', element: <BasicModeRestricted><NetWorthReport /></BasicModeRestricted> },
            { path: 'reports/spending-comparison', element: <BasicModeRestricted><SpendingComparison /></BasicModeRestricted> },
            { path: 'reports/budget-performance', element: <BasicModeRestricted><BudgetPerformance /></BasicModeRestricted> },
            {
                path: 'integrations',
                element: (
                    <BasicModeRestricted>
                        <Outlet />
                    </BasicModeRestricted>
                ),
                children: [
                    { path: 'boad-finance', element: <BoadFinance /> },
                ],
            },
            {
                path: 'admin',
                element: (
                    <AdminRequired>
                        <ErrorBoundary>
                            <Outlet />
                        </ErrorBoundary>
                    </AdminRequired>
                ),
                children: [
                    { index: true, element: <AdminDashboard /> },
                    { path: 'overview', element: <AdminDashboard /> },
                    { path: 'referrals', element: <AdminReferrals /> },
                    { path: 'users', element: <AdminUsers /> },
                    { path: 'finance', element: <AdminFinance /> },
                    { path: 'settings', element: <AdminSettings /> },
                    { path: 'offers', element: <AdminOffers /> },
                ]
            },
        ],
    },
    {
        element: (
            <LogoutNeeded>
                <ErrorBoundary>
                    <BlankLayout />
                </ErrorBoundary>
            </LogoutNeeded>
        ),
        children: [
            {
                path: '/login',
                element: <Login />,
            },
            {
                path: '/register',
                element: <Register />,
            },
            {
                path: '/forgot-password',
                element: <ForgotPassword />,
            }
        ],
    },
    {
        element: (
            <ErrorBoundary>
                <BlankLayout />
            </ErrorBoundary>
        ),
        children: [
            {
                path: '/partner-program/terms-and-conditions',
                element: <TermsAndCondition />,
            },
            {
                path: 'payment-status',
                element: <PaymentStatus />,
            },
            {
                path: 'email-verification',
                element: <EmailVerificationResult />,
            },
            {
                path: 'auth/action',
                element: <AuthActionHandler />,
            },
            {
                path: 'verify-email',
                element: <PendingVerification />,
            },
            {
                path: 'auth/yandex/callback',
                element: <YandexCallback />,
            },
            {
                path: '*',
                element: <ERROR404 />,
            },
        ]
    }
];

export { routes };
